<script setup>
import { ref, watch, onMounted, computed } from 'vue';

import format from 'date-fns/format';
import getDay from 'date-fns/getDay';

import { getQuantileIntervals } from 'shared/utils/index.js';

import { groupHeatmapByDay } from 'helpers/ReportsDataHelper';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  heatData: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});
const { t } = useI18n();
const processedData = computed(() => {
  return groupHeatmapByDay(props.heatData);
});

const quantileRange = computed(() => {
  const flattenedData = props.heatData.map(data => data.value);
  return getQuantileIntervals(flattenedData, [0.2, 0.4, 0.6, 0.8, 0.9, 0.99]);
});

function getCountTooltip(value) {
  if (!value) {
    return t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.NO_CONVERSATIONS');
  }

  if (value === 1) {
    return t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.CONVERSATION', {
      count: value,
    });
  }

  return t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.CONVERSATIONS', {
    count: value,
  });
}

function formatDate(dateString) {
  return format(new Date(dateString), 'MMM d, yyyy');
}

function getDayOfTheWeek(date) {
  const dayIndex = getDay(date);
  const days = [
    t('DAYS_OF_WEEK.SUNDAY'),
    t('DAYS_OF_WEEK.MONDAY'),
    t('DAYS_OF_WEEK.TUESDAY'),
    t('DAYS_OF_WEEK.WEDNESDAY'),
    t('DAYS_OF_WEEK.THURSDAY'),
    t('DAYS_OF_WEEK.FRIDAY'),
    t('DAYS_OF_WEEK.SATURDAY'),
  ];
  return days[dayIndex];
}
function getHeatmapLevelClass(value) {
  if (!value) return 'outline-n-container dark:bg-n-vars-black-br/30 bg-slate-50/50';

  if (value <= 5) {
    return 'bg-[#A6F4C5]';
  } else if (value <= 10) {
    return 'bg-[#12B76A]'; 
  } else {
    return 'bg-[#F97066]'; 
  }
}
</script>

<template>
  <div
    class="grid w-full gap-x-4 sm:gap-[18px] md:gap-x-6 gap-y-[3px] grid-cols-[10%_1fr] ">
    <template v-if="loadingRef">
      <!-- Left: skeleton row labels (days of week + tiny date below) -->
      <div class="grid gap-[2px] md:gap-1 lg:gap-[6px] flex-shrink-0">
        <div v-for="ii in 7" :key="ii"
          class="flex flex-col items-start justify-between leading-none">
          <span class="bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse w-8 h-4 rounded-sm mb-1" />
          <span class="bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse w-12 h-2 rounded-sm" />
        </div>
      </div>
      <!-- Main grid skeleton -->
      <div class="grid gap-[1px] md:gap-1 lg:gap-[6px]">
        <div v-for="ii in 7" :key="ii"
          class="grid grid-cols-[repeat(24,_1fr)] gap-[2px] md:gap-1 lg:gap-[6px]">
          <div v-for="jj in 24" :key="jj"
            class="aspect-square rounded-[3px] bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse" />
        </div>
      </div>
      <div />
      <!-- Bottom: hour labels (mirror real markup's layout/classes) -->
      <div class="grid gap-[3px] flex-shrink-0">
        <div
          class="grid grid-cols-[repeat(24,_1fr)] gap-[1px] sm:gap-1 md:lg:gap-[6px] w-full text-[0.28rem] md:text-[0.54rem] lg:text-xs font-normal text-slate-800 dark:text-slate-200 leading-tight">
          <div v-for="ii in 24" :key="ii"
            class="text-center whitespace-nowrap bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse h-2 rounded" />
        </div>
        <!-- Bottom: color legend skeletons (bucket) -->
        <div class="py-1 md:py-2 lg:py-3 flex justify-end gap-2 md:gap-4 lg:gap-6">
          <div class="flex items-center gap-[2px] md:gap-[4px] lg:gap-[6px]">
            <span class="size-1 md:size-3 lg:size-[0.875rem] rounded-[1px] md:rounded-[3px] lg:rounded-[3px] bg-[#A6F4C5] animate-loader-pulse" />
            <span class="text-[0.5rem] md:text-xs text-n-slate-12 bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse rounded w-10 h-2 inline-block" />
          </div>
          <div class="flex items-center gap-[2px] md:gap-[4px] lg:gap-[6px]">
            <span class="size-1 md:size-3 lg:size-[0.875rem] rounded-[1px] md:rounded-[3px] lg:rounded-[3px] bg-[#12B76A] animate-loader-pulse" />
            <span class="text-[0.5rem] md:text-xs text-n-slate-12 bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse rounded w-10 h-2 inline-block" />
          </div>
          <div class="flex items-center gap-[2px] md:gap-[4px] lg:gap-[6px]">
            <span class="size-1 md:size-3 lg:size-[0.875rem] rounded-[1px] md:rounded-[3px] lg:rounded-[3px] bg-[#F97066] animate-loader-pulse" />
            <span class="text-[0.5rem] md:text-xs text-n-slate-12 bg-slate-100 dark:bg-n-gray-8 animate-loader-pulse rounded w-10 h-2 inline-block" />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="grid gap-[2px] md:gap-1 lg:gap-[6px] flex-shrink-0">
        <div v-for="dateKey in processedData.keys()" :key="dateKey"
          class="flex flex-col items-start justify-between leading-none">
          <span class="text-slate-800 dark:text-n-gray-3 text-[0.5rem] md:text-sm lg:text-base lg:font-semibold">
            {{ getDayOfTheWeek(new Date(dateKey)) }}
          </span>
          <time class="font-normal text-[0.36rem] md:text-[0.5rem] lg:text-xs text-slate-700 dark:text-n-gray-6">
            {{ formatDate(dateKey) }}
          </time>
        </div>
      </div>
      <div class="grid gap-[1px] md:gap-1 lg:gap-[6px]">
        <div v-for="dateKey in processedData.keys()" :key="dateKey" 
          class="grid gap-[2px] md:gap-1 lg:gap-[6px] grid-cols-[repeat(24,_1fr)]"
        >
          <div v-for="data in processedData.get(dateKey)" :key="data.timestamp"
            v-tooltip.top="getCountTooltip(data.value)"
          >
            <template v-if="!data.value">
              <div class="aspect-square relative dark:bg-n-gray-8">
                <div class="absolute inset-0">
                  <div 
                    class="absolute inset-[1px] rounded-[3px]"
                    :class="getHeatmapLevelClass(data.value)" 
                  />
                </div>
              </div>
            </template>            
            <template v-else>
              <div 
                class="aspect-square rounded-[3px]"
                :class="getHeatmapLevelClass(data.value)" 
              />
            </template>
          </div>
        </div>
      </div>
      <div />
      <div class="grid gap-[3px] flex-shrink-0">
        <div
          class="grid grid-cols-[repeat(24,_1fr)] gap-[1px] sm:gap-1 md:lg:gap-[6px] w-full text-[0.28rem] md:text-[0.54rem] lg:text-xs font-normal text-slate-800 dark:text-slate-200 leading-tight">
          <div v-for="ii in 24" :key="ii" class="text-center whitespace-nowrap">
            {{ ii - 1 }}–{{ ii }}
          </div>
        </div>
        <div class="py-1 md:py-2 lg:py-3 flex justify-end gap-2 md:gap-4 lg:gap-6">
          <div class="flex items-center gap-[2px] md:gap-[4px] lg:gap-[6px]">
            <span class="size-1 md:size-3 lg:size-[0.875rem] rounded-[1px] md:rounded-[3px] lg:rounded-[3px] bg-[#A6F4C5]"></span>
            <span class="text-[0.5rem] md:text-xs text-n-slate-12">{{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.BUCKET_LABELS.RANGE_0_5') }}</span>
          </div>
          <div class="flex items-center gap-[2px] md:gap-[4px] lg:gap-[6px]">
            <span class="size-1 md:size-3 lg:size-[0.875rem] rounded-[1px] md:rounded-[3px] lg:rounded-[3px] bg-[#12B76A]"></span>
            <span class="text-[0.5rem] md:text-xs text-n-slate-12">{{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.BUCKET_LABELS.RANGE_5_10') }}</span>
          </div>
          <div class="flex items-center gap-[2px] md:gap-[4px] lg:gap-[6px]">
            <span class="size-1 md:size-3 lg:size-[0.875rem] rounded-[1px] md:rounded-[3px] lg:rounded-[3px] bg-[#F97066]"></span>
            <span class="text-[0.5rem] md:text-xs text-n-slate-12">{{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.BUCKET_LABELS.RANGE_10_PLUS') }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
